import { Check, ChevronDown } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';
import type { SelectOption } from '@/types/formSchema';

import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface SearchableSelectProps {
  id?: string;
  name?: string;
  value: string | boolean | number;
  onValueChange: (value: string | boolean | number) => void;
  options: SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  /** Optional leading icon rendered inside the trigger, before the label. */
  triggerIcon?: React.ComponentType<{ className?: string }>;
  /** Optional trailing content rendered at the right of each option row. */
  renderOptionEnd?: (option: SelectOption) => React.ReactNode;
}

export function SearchableSelect({
  id,
  name,
  value,
  onValueChange,
  options,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  emptyText = 'No option found.',
  disabled = false,
  className,
  error = false,
  triggerIcon: TriggerIcon,
  renderOptionEnd,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);

  // Find the selected option to display its label
  // Use String() comparison to handle boolean/number values correctly
  const selectedOption = options.find(opt => String(opt.value) === String(value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          name={name}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between text-left font-normal',
            error && 'border-destructive focus:ring-destructive',
            className,
          )}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {TriggerIcon && <TriggerIcon className="h-4 w-4 shrink-0 opacity-70" />}
            <span
              className={cn(
                'truncate text-sm',
                selectedOption ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {selectedOption?.label || placeholder}
            </span>
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
        onWheel={e => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map(option => (
                <CommandItem
                  key={String(option.value)}
                  value={option.label}
                  onSelect={() => {
                    // Use String() comparison to handle boolean/number values
                    const isSelected = String(option.value) === String(value);
                    onValueChange(isSelected ? '' : option.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      String(value) === String(option.value) ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{option.label}</span>
                  {renderOptionEnd && (
                    <span className="ml-auto pl-2 shrink-0">{renderOptionEnd(option)}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
